import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "@/src/common/snackbar"
import { ConfirmDialog } from "@/src/common/custom-dialog"
import { deleteWorkspacePromptOverride, fetchWorkspaceOverrides } from "@/src/services/promptPolicies"
import {
    formatPolicyTypeName,
    formatTimestamp,
    isOverrideStale,
} from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { PromptPolicy, WorkspacePromptPolicyOverride } from "@/src/types/promptPolicy"
import { resolveWorkspaceDisplayName } from "@/src/features/internalUsers/utils/promptPolicyUtils"

import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import { Iconify } from "@/src/common/iconify"
import { LoadingButton } from "@mui/lab"

type WorkspacePromptOverridesDialogProps = {
    open: boolean
    policy: PromptPolicy | null
    currentGlobalVersion?: number | null
    workspaceNameById: Record<string, string>
    onClose: () => void
    onViewPrompt?: (override: WorkspacePromptPolicyOverride, workspaceName: string) => void
    onCompare?: (override: WorkspacePromptPolicyOverride, workspaceName: string) => void
    onSelectWorkspaceForEdit?: (workspaceId: string) => void
    onSuccess?: () => void
}

export function WorkspacePromptOverridesDialog({
    open,
    policy,
    currentGlobalVersion,
    workspaceNameById,
    onClose,
    onViewPrompt,
    onCompare,
    onSelectWorkspaceForEdit,
    onSuccess,
}: WorkspacePromptOverridesDialogProps) {
    const [overrides, setOverrides] = useState<WorkspacePromptPolicyOverride[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [resetTarget, setResetTarget] = useState<WorkspacePromptPolicyOverride | null>(null)
    const [isResetting, setIsResetting] = useState(false)

    const loadOverrides = useCallback(async () => {
        if (!policy) return
        setIsLoading(true)
        try {
            const data = await fetchWorkspaceOverrides(policy.name)
            setOverrides(data.overrides)
        } catch {
            setOverrides([])
        } finally {
            setIsLoading(false)
        }
    }, [policy])

    useEffect(() => {
        if (open && policy) {
            loadOverrides()
        }
    }, [open, policy, loadOverrides])

    const sortedOverrides = useMemo(
        () =>
            [...overrides].sort((a, b) => {
                const aTime = a.lastModifiedDate ?? a.createdDate ?? ""
                const bTime = b.lastModifiedDate ?? b.createdDate ?? ""
                return bTime.localeCompare(aTime)
            }),
        [overrides]
    )

    const handleConfirmReset = async () => {
        if (!policy || !resetTarget) return
        setIsResetting(true)
        try {
            await deleteWorkspacePromptOverride(policy.name, resetTarget.workspaceId)
            toast.success("Workspace reset to global prompt")
            setResetTarget(null)
            await loadOverrides()
            onSuccess?.()
        } catch {
            // toast in service
        } finally {
            setIsResetting(false)
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 6 }}>
                    <Box>
                        <Typography variant="h6">Workspace overrides</Typography>
                        {policy && (
                            <Typography variant="body2" color="text.secondary">
                                {formatPolicyTypeName(policy.policyType)}
                                {currentGlobalVersion != null && currentGlobalVersion > 0
                                    ? ` — global v${currentGlobalVersion}`
                                    : ""}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {isLoading ? (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={32} />
                        </Stack>
                    ) : sortedOverrides.length === 0 ? (
                        <Typography color="text.secondary" py={2}>
                            No workspace-specific overrides. All workspaces use the global prompt.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Workspace</TableCell>
                                    <TableCell>Based on global v</TableCell>
                                    <TableCell>Last modified</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedOverrides.map((row) => {
                                    const workspaceName = resolveWorkspaceDisplayName(row.workspaceId, workspaceNameById)
                                    const stale = isOverrideStale(row, currentGlobalVersion)
                                    return (
                                        <TableRow
                                            key={row.workspaceId}
                                            hover
                                            sx={
                                                stale
                                                    ? (theme) => ({
                                                          bgcolor:
                                                              theme.palette.mode === "light"
                                                                  ? "warning.50"
                                                                  : "warning.dark",
                                                      })
                                                    : undefined
                                            }
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="body2">{workspaceName}</Typography>
                                                    {stale && (
                                                        <Chip label="Stale" size="small" color="warning" />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {row.basedOnGlobalVersionNumber != null
                                                    ? `v${row.basedOnGlobalVersionNumber}`
                                                    : "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {row.lastModifiedDate
                                                        ? formatTimestamp(row.lastModifiedDate)
                                                        : row.createdDate
                                                          ? formatTimestamp(row.createdDate)
                                                          : "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    justifyContent="flex-end"
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => onViewPrompt?.(row, workspaceName)}
                                                    >
                                                        View
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => onCompare?.(row, workspaceName)}
                                                    >
                                                        Compare
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => {
                                                            onSelectWorkspaceForEdit?.(row.workspaceId)
                                                            onClose()
                                                        }}
                                                    >
                                                        Modify
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => setResetTarget(row)}
                                                    >
                                                        Reset
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!resetTarget}
                onClose={() => setResetTarget(null)}
                title="Reset to global prompt?"
                content={
                    <Typography variant="body2">
                        Remove the workspace override for{" "}
                        <strong>
                            {resetTarget
                                ? resolveWorkspaceDisplayName(resetTarget.workspaceId, workspaceNameById)
                                : "this workspace"}
                        </strong>
                        . LLM calls will use the global prompt for this policy.
                    </Typography>
                }
                action={
                    <LoadingButton
                        variant="contained"
                        color="warning"
                        loading={isResetting}
                        onClick={handleConfirmReset}
                    >
                        Reset to global
                    </LoadingButton>
                }
            />
        </>
    )
}
